// auth-token.interceptor.ts
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn
} from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "./auth.service";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { LoggerService } from "../../shared/services/logger.service";
import { throwError } from "rxjs";

const UNAUTHORIZED_ROUTE = "/unauthorized";
const MAX_UNAUTHORIZED_REFRESHES = 3;

export const authTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const auth = inject(AuthService);
  const logger = inject(LoggerService);

  // Skip public token endpoints and auth endpoints
  if (
    req.url.includes("/login") ||
    req.url.includes("/logout") ||
    req.url.includes("api/vendor/") ||
    req.url.includes("api/homeowner/")
  ) {
    return next(req);
  }

  return auth.validToken$.pipe(
    filter((t): t is string =>
      !!t),
    take(1),
    switchMap(token =>
      next(
        req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      )
    ),
    catchError(err => {
      const isAuthError = err && (err.status === 401 || err.status === 403);
      
      const isOffline = navigator.onLine === false;
      if (isAuthError && isOffline) {
        // Offline — auth error likely from NGSW not falling back to cache on HTTP errors.
        // Do not reload, as re-authentication is not possible without connectivity.
        logger.warn(`[AuthTokenInterceptor] Received ${err.status} while offline for ${req.url}. Skipping re-auth reload.`);
        return next(req);
      }
      
      if (isAuthError) {
        const unauthorizedRefreshCount = Number(sessionStorage.getItem("unauthorizedRefreshCount") || "0");
        const hasReachedMaxRefreshAttempts = unauthorizedRefreshCount >= MAX_UNAUTHORIZED_REFRESHES;
        if (hasReachedMaxRefreshAttempts) {
          logger.error("[AuthTokenInterceptor] Maximum unauthorized refresh attempts reached. Not reloading to prevent loop.");

          const shouldRedirectToUnauthorizedPage = window.location.pathname !== UNAUTHORIZED_ROUTE;
          if (shouldRedirectToUnauthorizedPage) {
            logger.error("[AuthTokenInterceptor] Redirecting to unauthorized page.");
            window.location.assign(UNAUTHORIZED_ROUTE);
            sessionStorage.removeItem("unauthorizedRefreshCount");
          }
          return next(req);
        }

        const shouldTryToReloadToReAuthenticate = window.location.pathname !== UNAUTHORIZED_ROUTE;
        if (shouldTryToReloadToReAuthenticate) {
          sessionStorage.setItem("unauthorizedRefreshCount", (Number(sessionStorage.getItem("unauthorizedRefreshCount") || "0") + 1).toString());
          logger.warn(`[AuthTokenInterceptor] Received 401/403 response when attempting to call ${req.url}. Reloading page to trigger re-authentication. Reload count: ${sessionStorage.getItem("unauthorizedRefreshCount")}`);
          window.location.reload();
        }
        return next(req);
      }
      return throwError(() => err);
    })
  );
};
